import { z } from 'zod';
import { apiUrl, getJson, sendJson } from '@/shared/api/client';
import { pageSchema } from '@/shared/api/page';

export const REASONS = [
  ['wrong_number', 'Wrong number'],
  ['out_of_date', 'Out of date'],
  ['wrong_source', 'Wrong or missing source'],
  ['missing', 'Missed the point'],
  ['other', 'Other'],
] as const;
export type Reason = (typeof REASONS)[number][0];

export const feedbackSchema = z.object({
  id: z.string(),
  question: z.string(),
  answer: z.string(),
  route: z.string(),
  verdict: z.enum(['up', 'down']),
  reason: z.string().nullable(),
  reasonLabel: z.string().nullable(),
  note: z.string().nullable(),
  channel: z.enum(['portal', 'teams']),
  userName: z.string(),
  createdAt: z.string(),
  // The answer's sources as they were sent: portal answers carry report and page, a Teams card may carry none.
  citations: z.array(z.unknown()).default([]),
  reviewedAt: z.string().nullish().default(null),
  reviewedBy: z.string().nullish().default(null),
});
export type Feedback = z.infer<typeof feedbackSchema>;

export type FeedbackIn = {
  question: string;
  answer: string;
  route: string;
  citations: unknown[];
  verdict: 'up' | 'down';
  reason?: Reason;
  note?: string;
};

export const giveFeedback = (body: FeedbackIn) => sendJson('post', '/feedback', feedbackSchema, body);

export const fetchFeedback = (params: { page: number; pageSize: number; verdict: string }, signal?: AbortSignal) =>
  getJson('/feedback', pageSchema(feedbackSchema), { params, ...(signal ? { signal } : {}) });

const countsSchema = z.object({ up: z.number().int(), down: z.number().int(), unreviewed: z.number().int().default(0) });
export type FeedbackCounts = z.infer<typeof countsSchema>;

export const fetchFeedbackCounts = (signal?: AbortSignal) => getJson('/feedback/counts', countsSchema, signal ? { signal } : {});

/** Marks a reported problem as dealt with. */
export const reviewFeedback = (id: string) => sendJson('post', `/feedback/${id}/review`, feedbackSchema, {});

/** Flagged questions as candidate accuracy-test cases, for an analyst to complete with the expected answer.
 * With an id, just that one flag. */
export const evalCasesUrl = (id?: string) => apiUrl(id ? `/feedback/eval-cases.csv?id=${encodeURIComponent(id)}` : '/feedback/eval-cases.csv');

const sourceSchema = z.object({ documentId: z.string(), title: z.string(), page: z.number().int() });

/** The answer's first source with a report and page, for "Open source". */
export const firstSource = (f: Feedback) => {
  for (const c of f.citations) {
    const parsed = sourceSchema.safeParse(c);
    if (parsed.success) return parsed.data;
  }
  return null;
};
